import {
  buildServiceTypeItems,
  buildTimingsFromSegments,
  DEFAULT_FORM_VALUES,
  IAppointmentOverBookingV2ModalFields,
  ITimeSegment,
  normalizeRequestData,
} from "./helpers";
import {
  IRotaManagementShiftListData,
  IRotaManagementShiftTimings,
  IRotaOverbookingRequest,
} from "@/api_framework/api_modals/Rota";
import { useForm } from "react-hook-form";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { format, isBefore, startOfDay } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IRotaState } from "@/app_redux/reducers/slices/auth/rota_slice";
import { setProvider } from "@/app_redux/reducers/slices/auth/rota_slice";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SimplePageHeading from "@/app/components/ui/PageHeadings/SimplePageHeading";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useRotaShiftNameAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftNameAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useRotaShiftCreateAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftCreateAPI";
import { useRotaUpdateImpactAPI } from "@/app/hooks/api_hooks/Rota/useRotaUpdationWarnAPI";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useRotaShiftProvidersAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftProviderAPI";
import OverbookingConfirmationModal from "@/app/components/routes/modals/OverbookingConfirmationModal";
import { useRotaOverbookingRequestCreateAPI } from "@/app/hooks/api_hooks/Rota/useRotaOverbookingRequestCreateAPI";
import { useRotaOverbookingRequestUpdateAPI } from "@/app/hooks/api_hooks/Rota/useRotaOverbookingRequestUpdateAPI";
import { useRotaOverbookingEligibleServicesAPI } from "@/app/hooks/api_hooks/Rota/useRotaOverbookingEligibleServicesAPI";

export type OverbookingModalMode = "new" | "update";

interface IOverbookingRequestPageProps {
  mode: OverbookingModalMode;
  requestData?: IRotaOverbookingRequest | null;
  initialFilters?: {
    clinicId?: string;
    date?: string;
    type?: string;
  };
  onCancel: () => void;
  onSaved: () => void;
}

const FormRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-6 py-4">
    <span className="w-[148px] shrink-0 text-xs font-medium text-[#1F1F23] pt-[9px] leading-tight">{label}</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

const ReadOnlyInput = ({ value, placeholder = "-" }: { value?: string; placeholder?: string }) => (
  <input
    type="text"
    value={value || ""}
    readOnly
    placeholder={placeholder}
    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 focus:outline-none cursor-not-allowed"
  />
);

const AppointmentOverBookingV2RequestPage: React.FC<IOverbookingRequestPageProps> = ({
  mode,
  requestData,
  initialFilters,
  onCancel,
  onSaved,
}) => {
  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);
  const rotaState: Partial<IRotaState> = routeState?.state ?? {};
  const clinics = rotaState?.clinics ?? [];
  const provider = rotaState?.provider ?? [];
  const rotaShiftName = rotaState?.rotaShiftName ?? [];
  const overbookingEligibleServices = rotaState?.overbookingEligibleServices ?? [];
  const fetchCities = useOrgCitiesListAPI("ROTA");
  const { execute: fetchProviders } = useRotaShiftProvidersAPI();
  const fetchShiftNames = useRotaShiftNameAPI();
  const fetchEligibleServices = useRotaOverbookingEligibleServicesAPI();
  const createOverbookingRequest = useRotaOverbookingRequestCreateAPI();
  const updateOverbookingRequest = useRotaOverbookingRequestUpdateAPI();
  const { execute: warnOverbookingImpact, loading: isWarnImpactInProgress } = useRotaUpdateImpactAPI();
  const { execute: createAdhocShift } = useRotaShiftCreateAPI();
  const isMutationInProgress = useLoader("isOverbookingRequestMutationInProgress");
  const arrayConstants = useArrayConstants();
  const startTimeItem = (arrayConstants.overBookingStartTimeItems ?? []) as ISelectMenuItemData[];
  const startTimeMinuteItem = (arrayConstants.overBookingStartTimeMinuteItems ?? []) as ISelectMenuItemData[];
  const [addAdhocShift, setAddAdhocShift] = useState(false);
  const [isUpdateConfirmOpen, setIsUpdateConfirmOpen] = useState(false);
  const [updateImpactCount, setUpdateImpactCount] = useState<number | null>(null);
  const [pendingUpdateShiftId, setPendingUpdateShiftId] = useState<string>("");
  const [pendingUpdateAdhocTimings, setPendingUpdateAdhocTimings] = useState<IRotaManagementShiftTimings[] | null>(
    null,
  );
  const [slotSegments, setSlotSegments] = useState<ITimeSegment[]>([]);
  const normalizedRequestData = useMemo(() => normalizeRequestData(requestData), [requestData]);
  const clinicItems = useSelectMenuReducer(clinics, "display_name", "id");
  const providerItems = useSelectMenuReducer(provider, "name", "staff_id");
  const shiftItems = useSelectMenuReducer(rotaShiftName, "shift_data_transformed", "id");

  const formHook = useForm<IAppointmentOverBookingV2ModalFields>({
    mode: "onChange",
    defaultValues: DEFAULT_FORM_VALUES,
  });

  useEffect(() => {
    fetchCities({
      withClinics: true,
      withClinicProviders: false,
      clinicOptions: { selectAll: true },
    });
    fetchShiftNames();
    fetchEligibleServices();
  }, []);

  useEffect(() => {
    const isUpdate = mode === "update";
    formHook.reset({
      ...DEFAULT_FORM_VALUES,
      clinicId: isUpdate ? normalizedRequestData.clinicId : initialFilters?.clinicId || "",
      clinicName: isUpdate ? normalizedRequestData.clinicName : "",
      date: isUpdate ? normalizedRequestData.requestDate : initialFilters?.date || DEFAULT_FORM_VALUES.date,
      type: isUpdate ? normalizedRequestData.type : initialFilters?.type || "",
      providerId: isUpdate ? normalizedRequestData.providerId : "",
      providerName: isUpdate ? normalizedRequestData.providerName : "",
      shift: isUpdate ? normalizedRequestData.shiftId : "",
    });
    setAddAdhocShift(false);
    setSlotSegments([]);
  }, [mode, normalizedRequestData, formHook, initialFilters?.clinicId, initialFilters?.date, initialFilters?.type]);

  useEffect(() => {
    if (mode !== "new") return;
    const clinicId = formHook.getValues("clinicId");
    const clinicName = formHook.getValues("clinicName");
    if (!clinicId || clinicName) return;
    const selectedClinic = clinicItems?.find((item) => item.id === clinicId);
    if (selectedClinic?.title) {
      formHook.setValue("clinicName", selectedClinic.title, { shouldValidate: true });
    }
  }, [mode, clinicItems, formHook]);

  const selectedType = formHook.watch("type");
  const selectedClinicId = formHook.watch("clinicId");
  const selectedClinicName = formHook.watch("clinicName");
  const selectedProviderId = formHook.watch("providerId");
  const selectedProviderName = formHook.watch("providerName");
  const selectedDate = formHook.watch("date");
  const selectedShift = formHook.watch("shift");
  const isMedicalTypeSelected = selectedType?.trim()?.toLowerCase() === "medical";
  const isUpdateMode = mode === "update";
  const showProviderField = mode === "new" ? isMedicalTypeSelected && Boolean(selectedClinicId) : isMedicalTypeSelected;
  const submitRef = useRef(false);

  useEffect(() => {
    if (mode !== "new" || !isMedicalTypeSelected) return;
    formHook.setValue("providerId", "");
    formHook.setValue("providerName", "");
    if (!selectedClinicId) {
      dispatch(
        setProvider({
          data: [],
        }),
      );
      return;
    }
    dispatch(
      setProvider({
        data: [],
      }),
    );
    fetchProviders(
      {
        clinic_ids: [selectedClinicId],
      },
      "overbooking_v2",
    );
  }, [dispatch, fetchProviders, formHook, isMedicalTypeSelected, mode, selectedClinicId]);

  useEffect(() => {
    if (mode !== "new" || isMedicalTypeSelected) return;
    formHook.setValue("providerId", "");
    formHook.setValue("providerName", "");
    dispatch(
      setProvider({
        data: [],
      }),
    );
  }, [dispatch, formHook, isMedicalTypeSelected, mode]);

  const serviceTypeItems = useMemo(() => {
    return buildServiceTypeItems(overbookingEligibleServices ?? []);
  }, [overbookingEligibleServices]);

  const hasPendingAdhocTiming = () => {
    const v = formHook.getValues();
    return Boolean(v.startTimeHour && v.startTimeMinute && v.endTimeHour && v.endTimeMinute);
  };

  const canSubmit = () => {
    const v = formHook.getValues();
    if (mode === "new") {
      if (!v.clinicId || !v.date || !v.type) return false;
      if (showProviderField && !v.providerId) return false;
    }
    if (addAdhocShift) return slotSegments.length > 0 || hasPendingAdhocTiming();
    return Boolean(v.shift);
  };

  const addTimeSegment = () => {
    const v = formHook.getValues();
    setSlotSegments((prev) => [
      ...prev,
      {
        startTimeHour: v.startTimeHour,
        startTimeMinute: v.startTimeMinute,
        endTimeHour: v.endTimeHour,
        endTimeMinute: v.endTimeMinute,
      },
    ]);
    formHook.setValue("startTimeHour", "");
    formHook.setValue("startTimeMinute", "");
    formHook.setValue("endTimeHour", "");
    formHook.setValue("endTimeMinute", "");
  };

  const removeTimeSegment = (index: number) => {
    setSlotSegments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const getPendingAdhocTimings = (): IRotaManagementShiftTimings[] => {
    const v = formHook.getValues();
    const timings: IRotaManagementShiftTimings[] = buildTimingsFromSegments(slotSegments);
    if (v.startTimeHour && v.startTimeMinute && v.endTimeHour && v.endTimeMinute) {
      timings.push({
        start_time: `${v.startTimeHour}:${v.startTimeMinute}`,
        end_time: `${v.endTimeHour}:${v.endTimeMinute}`,
      });
    }
    return timings;
  };

  const getSelectedShiftTimings = (shiftId: string): IRotaManagementShiftTimings[] => {
    const selectedShift = rotaShiftName.find((shift: any) => `${shift?.id}` === `${shiftId}`) as any;
    return (selectedShift?.shift_timings ?? []) as IRotaManagementShiftTimings[];
  };

  const getWarnPayloadForUpdate = (shiftId: string, adhocTimings?: IRotaManagementShiftTimings[]) => {
    const { clinicId, staffId, rotaDate, rotaId } = normalizedRequestData.warnContext;
    if (!clinicId || !staffId || !rotaDate || !rotaId) return null;
    const shiftTimings = adhocTimings ?? getSelectedShiftTimings(shiftId);
    return {
      clinic_id: clinicId,
      staff_id: staffId,
      rota_date: rotaDate,
      rota_id: rotaId,
      shift_timings: (shiftTimings ?? []).map(({ start_time, end_time }) => ({ start_time, end_time })),
    };
  };

  const createAdhocShiftAndGetId = async (timings: IRotaManagementShiftTimings[]): Promise<string | null> => {
    const adhocPayload: IRotaManagementShiftListData = {
      shift_name: "Adhoc Shift",
      shift_status: true,
      shift_type: "adhoc",
      shift_timings: timings,
    };
    const response = await createAdhocShift(adhocPayload);
    if (!response?.status || !response?.shiftId) return null;
    return response.shiftId;
  };

  const submitUpdateRequest = async (shiftId: string) => {
    const requestId = normalizedRequestData.requestId;
    if (!requestId) return;
    const response = await updateOverbookingRequest(requestId, { shift_id: shiftId });
    if (response?.status) onSaved();
  };

  const handleSave = async () => {
    if (submitRef.current || !canSubmit()) return;
    const fieldsToValidate: Array<keyof IAppointmentOverBookingV2ModalFields> =
      mode === "new" ? ["clinicId", "date", "type", "shift"] : ["shift"];
    if (mode === "new" && showProviderField) {
      fieldsToValidate.push("providerId");
    }
    if (addAdhocShift) {
      fieldsToValidate.splice(fieldsToValidate.indexOf("shift"), 1);
    }
    const isValid = await formHook.trigger(fieldsToValidate);
    if (!isValid) return;
    submitRef.current = true;
    try {
      const v = formHook.getValues();
      let shiftId = v.shift;
      let adhocTimings: IRotaManagementShiftTimings[] = [];
      if (addAdhocShift) {
        if (slotSegments.length === 0 && !hasPendingAdhocTiming()) {
          return;
        }
        adhocTimings = getPendingAdhocTimings();
      }
      if (!shiftId && !addAdhocShift) {
        return;
      }
      if (mode === "new") {
        if (addAdhocShift) {
          const createdShiftId = await createAdhocShiftAndGetId(adhocTimings);
          if (!createdShiftId) {
            return;
          }
          shiftId = createdShiftId;
        }
        const response = await createOverbookingRequest({
          clinic_id: v.clinicId,
          request_date: v.date,
          service_type: v.type,
          shift_id: shiftId,
          staff_id: showProviderField ? v.providerId : undefined,
        });
        if (response?.status) onSaved();
        return;
      }
      if (addAdhocShift) {
        const warnPayload = getWarnPayloadForUpdate("", adhocTimings);
        if (!warnPayload) {
          const createdShiftId = await createAdhocShiftAndGetId(adhocTimings);
          if (!createdShiftId) return;
          await submitUpdateRequest(createdShiftId);
          return;
        }
        const warnResponse = await warnOverbookingImpact(warnPayload as any);
        setUpdateImpactCount(warnResponse?.status ? (warnResponse?.data?.booking_count ?? 0) : null);
        setPendingUpdateShiftId("");
        setPendingUpdateAdhocTimings(adhocTimings);
        setIsUpdateConfirmOpen(true);
        return;
      }
      const warnPayload = getWarnPayloadForUpdate(shiftId);
      if (!warnPayload) {
        await submitUpdateRequest(shiftId);
        return;
      }
      const warnResponse = await warnOverbookingImpact(warnPayload as any);
      setUpdateImpactCount(warnResponse?.status ? (warnResponse?.data?.booking_count ?? 0) : null);
      setPendingUpdateShiftId(shiftId);
      setPendingUpdateAdhocTimings(null);
      setIsUpdateConfirmOpen(true);
    } finally {
      submitRef.current = false;
    }
  };

  const renderRequestDetailsFields = () => (
    <>
      <FormRow label={isUpdateMode ? "Clinic" : "Select Clinic"}>
        {isUpdateMode ? (
          <ReadOnlyInput
            value={selectedClinicName || clinicItems?.find((item) => item.id === selectedClinicId)?.title || ""}
          />
        ) : (
          <>
            <input hidden {...formHook.register("clinicId", { required: true })} />
            <SearchableSelectMenu
              errorMessages={[{ message: "Clinic is required", type: "required" }]}
              onSelectItem={(item) => {
                formHook.setValue("clinicId", item?.id ?? "");
                formHook.setValue("clinicName", item?.title ?? "");
              }}
              fieldError={formHook.formState.errors.clinicName}
              register={formHook.register("clinicName", { required: true })}
              selectItems={clinicItems ?? []}
              placeholder="Select Clinic"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={clinicItems?.find((item) => item.id === selectedClinicId)}
              listBoxClassName="w-full"
              className="text-xs text-gray-700"
              containerClassName="w-full"
            />
          </>
        )}
      </FormRow>
      <FormRow label={isUpdateMode ? "Date" : "Select Date"}>
        {isUpdateMode ? (
          <ReadOnlyInput value={selectedDate} />
        ) : (
          <PWLCalendarComponent
            errorMessages={[{ message: "Date is required", type: "required" }]}
            register={formHook.register("date", { required: true })}
            fieldError={formHook.formState.errors.date}
            showTypedErrors={false}
            showLeftNav={false}
            allowPastDaySelection={false}
            disablePastMonthSelection
            disableDatesBeforeDate={format(new Date(), "yyyy-MM-dd")}
            defaultSelectedDate={selectedDate}
            onSelectDate={(date) => {
              if (!date || isBefore(startOfDay(date), startOfDay(new Date()))) return;
              formHook.setValue("date", format(date, "yyyy-MM-dd"));
            }}
            className="w-full py-2 text-sm focus:outline-none !focus:ring-0 !ring-gray-300"
          />
        )}
      </FormRow>
      <FormRow label={isUpdateMode ? "Type" : "Select Type"}>
        {isUpdateMode ? (
          <ReadOnlyInput value={selectedType} />
        ) : (
          <>
            <input hidden {...formHook.register("type")} />
            <SearchableSelectMenu
              errorMessages={[{ message: "Type is required", type: "required" }]}
              onSelectItem={(item) => {
                formHook.setValue("type", item?.id ?? "");
              }}
              fieldError={formHook.formState.errors.type}
              register={formHook.register("type", { required: true })}
              selectItems={serviceTypeItems ?? []}
              placeholder="Select Type"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={serviceTypeItems?.find((item) => item.id === selectedType)}
              listBoxClassName="w-full"
              className="text-xs text-gray-700"
              containerClassName="w-full"
            />
          </>
        )}
      </FormRow>
      {showProviderField && (
        <FormRow label={isUpdateMode ? "Provider" : "Select Provider"}>
          {isUpdateMode ? (
            <ReadOnlyInput
              value={selectedProviderName || providerItems?.find((item) => item.id === selectedProviderId)?.title || ""}
            />
          ) : (
            <>
              <input hidden {...formHook.register("providerId", { required: true })} />
              <SearchableSelectMenu
                errorMessages={[{ message: "Provider is required", type: "required" }]}
                onSelectItem={(item) => {
                  formHook.setValue("providerId", item?.id ?? "");
                  formHook.setValue("providerName", item?.title ?? "");
                }}
                fieldError={formHook.formState.errors.providerName}
                register={formHook.register("providerName", { required: true })}
                selectItems={providerItems ?? []}
                placeholder="Select Provider"
                showTooltips={false}
                showTypedErrors={false}
                showDropdownIcon
                defaultSelected={providerItems?.find((item) => item.id === selectedProviderId)}
                listBoxClassName="w-full"
                className="text-xs text-gray-700"
                containerClassName="w-full"
                showDropdownWhenNoData
              />
            </>
          )}
        </FormRow>
      )}
    </>
  );

  return (
    <div className="bg-white px-4 py-4 pb-8">
      <div className="mx-auto w-full">
        <SimplePageHeading
          description={
            mode === "new"
              ? "Create overbooking request for eligible clinic, service and shift"
              : "Update the shift for this overbooking request"
          }
          title={mode === "new" ? "Create Overbooking Request" : "Update Overbooking Request"}
        />
        <div className="w-full lg:w-2/4 mt-4 mx-auto border border-gray-50 shadow-lg rounded-lg p-4 bg-white">
          {renderRequestDetailsFields()}
          <FormRow label="Select Shift">
            {!addAdhocShift ? (
              <>
                <input hidden {...formHook.register("shift")} />
                <SearchableSelectMenu
                  errorMessages={[{ message: "Shift is required", type: "required" }]}
                  onSelectItem={(item) => {
                    formHook.setValue("shift", item?.id ?? "");
                  }}
                  fieldError={formHook.formState.errors.shift}
                  register={formHook.register("shift", { required: !addAdhocShift })}
                  selectItems={shiftItems ?? []}
                  placeholder="Select Shift Name"
                  showTooltips={false}
                  showTypedErrors={false}
                  showDropdownIcon
                  defaultSelected={shiftItems?.find((item) => item.id === selectedShift)}
                  listBoxClassName="w-full"
                  className="text-xs text-gray-700"
                  containerClassName="w-full"
                  addAction={{
                    title: "+ Add Adhoc",
                    callBack: () => {
                      formHook.setValue("shift", "");
                      setAddAdhocShift(true);
                    },
                  }}
                />
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-4">
                  <div className="flex-1 space-y-1">
                    <p className="text-xs font-semibold text-[#1F1F23]">Start Time</p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <NonSearchableSelectMenu
                          errorMessages={[{ message: "Hour is required", type: "required" }]}
                          onSelectItem={(item) => formHook.setValue("startTimeHour", item?.title ?? "")}
                          fieldError={formHook.formState.errors.startTimeHour}
                          register={formHook.register("startTimeHour")}
                          selectItems={startTimeItem}
                          placeholder="HH"
                          showTooltips={false}
                          showTypedErrors={false}
                          showDropdownIcon
                          defaultSelected={startTimeItem?.find(
                            (item) => item.title === formHook.watch("startTimeHour"),
                          )}
                          listBoxClassName="w-full"
                          className="text-gray-700"
                          containerClassName="w-full"
                        />
                      </div>
                      <div className="flex-1">
                        <NonSearchableSelectMenu
                          errorMessages={[{ message: "Minute is required", type: "required" }]}
                          onSelectItem={(item) => formHook.setValue("startTimeMinute", item?.title ?? "")}
                          fieldError={formHook.formState.errors.startTimeMinute}
                          register={formHook.register("startTimeMinute")}
                          selectItems={startTimeMinuteItem}
                          placeholder="MM"
                          showTooltips={false}
                          showTypedErrors={false}
                          showDropdownIcon
                          defaultSelected={startTimeMinuteItem?.find(
                            (item) => item.title === formHook.watch("startTimeMinute"),
                          )}
                          listBoxClassName="w-full"
                          className="text-gray-700"
                          containerClassName="w-full"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-xs font-semibold text-[#1F1F23]">End Time</p>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                        <NonSearchableSelectMenu
                          errorMessages={[{ message: "Hour is required", type: "required" }]}
                          onSelectItem={(item) => formHook.setValue("endTimeHour", item?.title ?? "")}
                          fieldError={formHook.formState.errors.endTimeHour}
                          register={formHook.register("endTimeHour")}
                          selectItems={startTimeItem}
                          placeholder="HH"
                          showTooltips={false}
                          showTypedErrors={false}
                          showDropdownIcon
                          defaultSelected={startTimeItem?.find((item) => item.title === formHook.watch("endTimeHour"))}
                          listBoxClassName="w-full"
                          className="text-gray-700"
                          containerClassName="w-full"
                        />
                      </div>
                      <div className="flex-1">
                        <NonSearchableSelectMenu
                          errorMessages={[{ message: "Minute is required", type: "required" }]}
                          onSelectItem={(item) => formHook.setValue("endTimeMinute", item?.title ?? "")}
                          fieldError={formHook.formState.errors.endTimeMinute}
                          register={formHook.register("endTimeMinute")}
                          selectItems={startTimeMinuteItem}
                          placeholder="MM"
                          showTooltips={false}
                          showTypedErrors={false}
                          showDropdownIcon
                          defaultSelected={startTimeMinuteItem?.find(
                            (item) => item.title === formHook.watch("endTimeMinute"),
                          )}
                          listBoxClassName="w-full"
                          className="text-gray-700"
                          containerClassName="w-full"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addTimeSegment}
                        className="h-9 w-9 shrink-0 rounded-md border border-gray-300 text-lg leading-none text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!hasPendingAdhocTiming()}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                {slotSegments.length > 0 && (
                  <div className="space-y-1.5">
                    {slotSegments.map((seg, index) => (
                      <div
                        key={`${seg.startTimeHour}${seg.startTimeMinute}${index}`}
                        className="flex items-center justify-between bg-gray-50 border border-gray-100 px-3 py-2 rounded-lg text-xs"
                      >
                        <span className="font-medium text-gray-700">
                          {seg.startTimeHour}:{seg.startTimeMinute}
                          <span className="mx-1.5 text-gray-400">→</span>
                          {seg.endTimeHour}:{seg.endTimeMinute}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeTimeSegment(index)}
                          className="text-red-500 hover:text-red-600 font-medium ml-4"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </FormRow>
          <div className="pt-5 mt-2 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isMutationInProgress}
              className="px-4 py-2 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSubmit() || isMutationInProgress}
              className="px-4 py-2 rounded-lg text-xs font-medium bg-black text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isMutationInProgress ? "Saving..." : mode === "new" ? "Save" : "Update"}
            </button>
          </div>
        </div>
      </div>
      <OverbookingConfirmationModal
        open={isUpdateConfirmOpen}
        variant="update_overbooking_request"
        onClose={() => {
          setIsUpdateConfirmOpen(false);
          setPendingUpdateShiftId("");
          setPendingUpdateAdhocTimings(null);
          setUpdateImpactCount(null);
        }}
        disableClose={isWarnImpactInProgress || isMutationInProgress}
        impactCount={updateImpactCount}
        onConfirm={async () => {
          if (isWarnImpactInProgress || isMutationInProgress) return;
          let finalShiftId = pendingUpdateShiftId;
          if (pendingUpdateAdhocTimings?.length) {
            const createdShiftId = await createAdhocShiftAndGetId(pendingUpdateAdhocTimings);
            if (!createdShiftId) return;
            finalShiftId = createdShiftId;
          }
          if (!finalShiftId) return;
          await submitUpdateRequest(finalShiftId);
          setIsUpdateConfirmOpen(false);
          setPendingUpdateShiftId("");
          setPendingUpdateAdhocTimings(null);
          setUpdateImpactCount(null);
        }}
        confirmDisabled={isWarnImpactInProgress || isMutationInProgress}
        cancelDisabled={isWarnImpactInProgress || isMutationInProgress}
        confirmText={isMutationInProgress ? "Updating..." : "Update"}
        panelMaxWidthClassName="sm:max-w-[45rem]"
      />
    </div>
  );
};

export default AppointmentOverBookingV2RequestPage;
